import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

/**
 * Single source of truth for the compact "grid look" shared by MUI `Table`s
 * across the app (agent tables and cards, settings, exports, message metrics)
 * and the /markets BondDataGrid: body 9.75/400, uppercase header 9/600, 25.7px
 * rows, 10px horizontal cell padding, and borders/colors from
 * `theme.app.dataGrid`, so cell typography reads identically to the live grid.
 */

/** Default body-cell text (issuer, coupon, dates, yields, quantity, …). */
export const GRID_ROW_FONT_SIZE_PX = 9.75;
export const GRID_ROW_FONT_WEIGHT = 400;
/** Row height matching BondDataGrid density on /markets. */
export const GRID_ROW_HEIGHT_PX = 25.7;
/** Column-header title (semi-bold, uppercase). */
export const GRID_HEADER_FONT_SIZE_PX = 9;
export const GRID_HEADER_FONT_WEIGHT = 600;
/** Horizontal cell inset matching the grid. */
export const GRID_CELL_PX = "10px";
/**
 * Outer corner radius, in MUI `sx` units (× `theme.shape.borderRadius` = 8px),
 * so agent tables read like the outlined agent cards (`CardShell`).
 */
export const GRID_TABLE_BORDER_RADIUS = 1;

/**
 * Column-header / label typography (uppercase, semi-bold) shared by card and
 * table titles so every "grid header" text reads identically. Spread into an
 * `sx` and add per-site extras (spacing, etc.).
 */
export function makeGridHeaderLabelSx(theme: Theme): SystemStyleObject<Theme> {
  return {
    fontFamily: "var(--font-sans)",
    fontSize: GRID_HEADER_FONT_SIZE_PX,
    fontWeight: GRID_HEADER_FONT_WEIGHT,
    textTransform: "uppercase",
    color: theme.app.dataGrid.headerTextColor,
  };
}

/**
 * Body-cell / value typography shared by card values and descriptions so every
 * "grid cell" text reads identically. Spread into an `sx` and add per-site
 * extras (tabular numbers, muted color, spacing, etc.).
 */
export function makeGridCellValueSx(theme: Theme): SystemStyleObject<Theme> {
  return {
    fontFamily: "var(--font-sans)",
    fontSize: GRID_ROW_FONT_SIZE_PX,
    fontWeight: GRID_ROW_FONT_WEIGHT,
    color: theme.app.dataGrid.cellTextColor,
  };
}

/**
 * Outlined `TableContainer` chrome shared by GFM markdown tables and MUI
 * `data_table` payloads: rounded frame, markets-grid border color, horizontal
 * scroll for wide tables (still clips the header fill to the corners).
 */
export function makeGridTableContainerSx(theme: Theme): SystemStyleObject<Theme> {
  return {
    overflowX: "auto",
    borderRadius: GRID_TABLE_BORDER_RADIUS,
    borderColor: theme.app.dataGrid.rowBorderColor,
  };
}

/**
 * `sx` for a MUI `<Table>` that renders the /markets grid look. Applied on the
 * `Table` element so it cascades to every `TableCell` (head and body) without
 * per-cell overrides. Last row / last column borders are dropped so the outlined
 * `TableContainer` supplies the outer frame.
 */
export function makeGridTableSx(theme: Theme): SystemStyleObject<Theme> {
  const dg = theme.app.dataGrid;
  return {
    m: 0,
    borderCollapse: "collapse",
    fontFamily: "var(--font-sans)",
    fontSize: GRID_ROW_FONT_SIZE_PX,
    fontWeight: GRID_ROW_FONT_WEIGHT,
    color: dg.cellTextColor,
    letterSpacing: "normal",
    "& .MuiTableCell-root": {
      fontFamily: "var(--font-sans)",
      fontSize: GRID_ROW_FONT_SIZE_PX,
      fontWeight: GRID_ROW_FONT_WEIGHT,
      color: dg.cellTextColor,
      letterSpacing: "normal",
      // Body cells stay sentence-case; headers set uppercase below.
      textTransform: "none",
      borderBottom: `1px solid ${dg.rowBorderColor}`,
      borderRight: `1px solid ${dg.rowBorderColor}`,
      px: GRID_CELL_PX,
      py: 0,
      // Must be a px string: a bare number is theme-spacing (×8) and blows the row up.
      height: `${GRID_ROW_HEIGHT_PX}px`,
      lineHeight: `${GRID_ROW_HEIGHT_PX}px`,
      whiteSpace: "nowrap",
      fontVariantNumeric: "tabular-nums",
      verticalAlign: "middle",
    },
    "& .MuiTableCell-head": {
      // Header fill matches the agent card header (`CardShell` raised row).
      bgcolor: theme.app.aiAssist.raisedBg,
      color: dg.headerTextColor,
      fontSize: GRID_HEADER_FONT_SIZE_PX,
      fontWeight: GRID_HEADER_FONT_WEIGHT,
      textTransform: "uppercase",
    },
    "& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root": {
      borderBottom: "none",
    },
    "& .MuiTableCell-root:last-of-type": {
      borderRight: "none",
    },
  };
}
